import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { bonusTrackerAPI } from '../../services/api';
import { EmployeeOption, ProgramInfo, employeeLabel, money } from './bonusTypes';
import { inputLabel } from './RuleFields';

/** One earning being typed: an employee, the case it is for, how many (or how much), and why. */
export interface EarningDraft {
  key: number;
  employee: EmployeeOption | null;
  reference: string;
  quantity: string;
  amount: string;
  reason: string;
}

let nextKey = 1;
export const newEarning = (): EarningDraft => ({ key: nextKey++, employee: null, reference: '', quantity: '', amount: '', reason: '' });

/** What the program rule will pay for this row (a preview; the server calculates the real amount). */
export function previewAmount(program: ProgramInfo, row: EarningDraft): number | null {
  const typed = row.quantity.trim();
  if (program.method === 'FIXED') return Number(program.params.amount) || null;
  if (program.method === 'QUANTITY_RATE') {
    const q = typed === '' ? 1 : Number(typed);
    return Number.isInteger(q) && q > 0 && program.params.rate ? Math.round(q * program.params.rate * 100) / 100 : null;
  }
  if (program.method === 'PERCENTAGE') {
    const base = Number(typed);
    return typed !== '' && base > 0 && program.params.percent ? Math.round(base * program.params.percent) / 100 : null;
  }
  if (program.method === 'TIERED') {
    const result = Number(typed);
    if (typed === '' || !(result >= 0)) return null;
    const reached = (program.params.tiers || []).filter((t) => t.from <= result).pop();
    return reached ? reached.amount : null;
  }
  if (program.method === 'MANUAL') return Number(row.amount) > 0 ? Number(row.amount) : null;
  return null;
}

/** Mirrors the server: an amount outside the program limits is refused, never adjusted. */
function limitProblem(program: ProgramInfo, amount: number): string | null {
  if (program.minAmount != null && amount < program.minAmount) return `The amount is below the program minimum ${money(program.minAmount)}.`;
  if (program.maxAmount != null && amount > program.maxAmount) return `The amount is above the program maximum ${money(program.maxAmount)}.`;
  return null;
}

export function earningProblem(program: ProgramInfo, row: EarningDraft): string | null {
  if (!row.employee) return 'Pick the employee.';
  if (program.duplicatePolicy === 'REFERENCE' && !row.reference.trim()) return `${program.referenceLabel || 'The customer or account reference'} is required.`;
  if (!row.reason.trim()) return 'The reason is required.';
  const amount = previewAmount(program, row);
  if (amount === null) {
    if (program.method === 'MANUAL') return 'Enter the amount.';
    if (program.method === 'PERCENTAGE') return 'Enter the base amount.';
    if (program.method === 'TIERED') {
      const first = (program.params.tiers || [])[0];
      return row.quantity.trim() !== '' && first ? `The result does not reach the first level (${first.from}), so nothing is earned.` : 'Enter the result.';
    }
    return 'Enter a whole quantity above zero.';
  }
  return limitProblem(program, amount);
}

export const earningsReady = (program: ProgramInfo, rows: EarningDraft[]) => rows.length > 0 && rows.every((r) => !earningProblem(program, r));

export const earningsToLines = (rows: EarningDraft[]) => rows.map((r) => ({
  employeeId: r.employee!.id,
  reference: r.reference.trim() || null,
  quantity: r.quantity.trim() === '' ? 1 : Number(r.quantity),
  amount: r.amount ? Number(r.amount) : undefined,
  concept: r.reason.trim(),
}));

interface Props {
  program: ProgramInfo;
  currency: string;
  campaignId: string | null;
  range: { start: string; end: string } | null;
  rows: EarningDraft[];
  onRows: (rows: EarningDraft[]) => void;
  everyone?: boolean;
}

/** Rows of earnings for a program bonus. The same employee can appear in several rows (one per case). */
const EarningsEditor: React.FC<Props> = ({ program, currency, campaignId, range, rows, onRows, everyone }) => {
  const [options, setOptions] = useState<EmployeeOption[]>([]);
  const search = useCallback(async (text: string) => {
    try { setOptions(await bonusTrackerAPI.employees(text, campaignId || 'ALL', range ? { from: range.start, to: range.end } : undefined, everyone)); } catch { /* typing still works */ }
  }, [campaignId, range?.start, range?.end, everyone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { void search(''); }, [search]);

  const change = (key: number, patch: Partial<EarningDraft>) => onRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const total = rows.reduce((sum, r) => sum + (r.employee ? previewAmount(program, r) || 0 : 0), 0);

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {program.method === 'FIXED' && `Each case pays ${money(program.params.amount || 0, currency)}.`}
        {program.method === 'QUANTITY_RATE' && `Each one pays ${money(program.params.rate || 0, currency)}; enter how many.`}
        {program.method === 'MANUAL' && 'Enter the amount for each case.'}
        {program.method === 'PERCENTAGE' && `Each earning pays ${program.params.percent}% of the base amount you enter.`}
        {program.method === 'TIERED' && `Enter the ${(program.params.metricLabel || 'result').toLowerCase()}; the highest level it reaches pays.`}
        {program.duplicatePolicy === 'REFERENCE' && ' The same case cannot be paid twice.'}
        {program.capPerEmployee != null && ` Cap per employee in this bonus: ${money(program.capPerEmployee, currency)}.`}
      </Typography>
      {rows.map((row) => {
        const amount = previewAmount(program, row);
        const limit = amount === null ? null : limitProblem(program, amount);
        return (
          <Stack key={row.key} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'flex-start' }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 250, flexBasis: 250, flexShrink: 0 }}
              options={options}
              value={row.employee}
              getOptionLabel={employeeLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(x) => x}
              onInputChange={(_e, value, reason) => { if (reason === 'input') void search(value); }}
              onChange={(_e, value) => change(row.key, { employee: value })}
              renderInput={(params) => <TextField {...params} label="Employee" />}
            />
            <TextField size="small" sx={{ width: { md: 190 }, flexShrink: 0 }} label={program.referenceLabel || 'Customer / account'} required={program.duplicatePolicy === 'REFERENCE'}
              value={row.reference} onChange={(e) => change(row.key, { reference: e.target.value })} />
            {(program.method === 'QUANTITY_RATE' || program.method === 'PERCENTAGE' || program.method === 'TIERED') && (
              <TextField size="small" type="number" sx={{ width: program.method === 'QUANTITY_RATE' ? { md: 90 } : { md: 150 }, minWidth: 80, flexShrink: 0 }}
                label={inputLabel(program.method, program.params)} value={row.quantity} placeholder={program.method === 'QUANTITY_RATE' ? '1' : undefined}
                inputProps={{ min: 0, step: program.method === 'QUANTITY_RATE' ? 1 : 'any' }} onChange={(e) => change(row.key, { quantity: e.target.value })} />
            )}
            {program.method === 'MANUAL' && (
              <TextField size="small" type="number" sx={{ width: { md: 150 }, flexShrink: 0 }} label={`Amount (${currency})`} value={row.amount}
                onChange={(e) => change(row.key, { amount: e.target.value })} />
            )}
            <TextField size="small" required label="Reason" sx={{ flex: 1, minWidth: 160 }} value={row.reason} onChange={(e) => change(row.key, { reason: e.target.value })} />
            <Typography sx={{ minWidth: 100, pt: 1, textAlign: 'right', whiteSpace: 'nowrap' }} color={amount === null ? 'text.disabled' : limit ? 'error' : 'text.primary'} title={limit || undefined}>
              {amount === null ? '-' : money(amount, currency)}
            </Typography>
            <IconButton size="small" aria-label="Remove row" disabled={rows.length === 1} onClick={() => onRows(rows.filter((r) => r.key !== row.key))}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      })}
      {rows.some((r) => r.employee && previewAmount(program, r) !== null && limitProblem(program, previewAmount(program, r) as number)) && (
        <Typography variant="body2" color="error">
          An amount is outside the program limits. It is not adjusted automatically: fix the quantity, or ask a super user to change the program.
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button size="small" onClick={() => onRows([...rows, newEarning()])}>Add another earning</Button>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Total {money(total, currency)}</Typography>
      </Box>
    </Stack>
  );
};

export default EarningsEditor;
